/**
 * Server state, through TanStack Query. Keys start with the workspace id so
 * switching workspace never shows another workspace's cached data.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CrosslistState, ExtensionDevice, FinanceSummary, ShopReport, ShopsState, UnmatchedSale, ItemDetail, ItemList, ItemListQuery, Marketplace, MarketplaceFee, Me, Member, Photo,
  PhotoUploadTicket, SaleRow, Settings, Workspace,
} from "@sable/api-contract";
import type { SlotKey } from "@sable/domain";
import { request } from "./api.ts";
import type { PreparedPhoto } from "./images.ts";

export const keys = {
  me: ["me"] as const,
  marketplaces: ["marketplaces"] as const,
  workspace: (ws: string) => [ws, "workspace"] as const,
  settings: (ws: string) => [ws, "settings"] as const,
  fees: (ws: string) => [ws, "fees"] as const,
  members: (ws: string) => [ws, "members"] as const,
  items: (ws: string) => [ws, "items"] as const,
  item: (ws: string, id: string) => [ws, "item", id] as const,
  sales: (ws: string) => [ws, "sales"] as const,
  finance: (ws: string) => [ws, "finance"] as const,
  crosslist: (ws: string, id: string) => [ws, "crosslist", id] as const,
  devices: (ws: string) => [ws, "devices"] as const,
  unmatched: (ws: string) => [ws, "unmatched-sales"] as const,
  shops: (ws: string) => [ws, "shops"] as const,
  sms: (ws: string) => [ws, "sms"] as const,
  shopReport: (ws: string) => [ws, "shops", "report"] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => request<Me>("/me"), staleTime: 60_000 });

export const useMarketplaces = () =>
  useQuery({ queryKey: keys.marketplaces, queryFn: () => request<Marketplace[]>("/marketplaces"), staleTime: Infinity });

export const useWorkspace = (ws: string) =>
  useQuery({ queryKey: keys.workspace(ws), queryFn: () => request<Workspace>("/workspace", { workspace: ws }), staleTime: 60_000 });

export const useSettings = (ws: string) =>
  useQuery({ queryKey: keys.settings(ws), queryFn: () => request<Settings>("/settings", { workspace: ws }) });

export const useFees = (ws: string) =>
  useQuery({ queryKey: keys.fees(ws), queryFn: () => request<MarketplaceFee[]>("/fees", { workspace: ws }) });

export const useMembers = (ws: string) =>
  useQuery({ queryKey: keys.members(ws), queryFn: () => request<Member[]>("/workspace/members", { workspace: ws }) });

export type ItemFilters = Partial<Omit<ItemListQuery, "cursor" | "limit">>;

export const useItems = (ws: string, filters: ItemFilters) =>
  useInfiniteQuery({
    queryKey: [...keys.items(ws), filters],
    queryFn: ({ pageParam }) =>
      request<ItemList>("/items", { workspace: ws, query: { ...filters, cursor: pageParam, limit: 48 } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

/** Polls while the AI is reading the item's photos, so its fields appear when it's done. */
export const useItem = (ws: string, id: string) =>
  useQuery({
    queryKey: keys.item(ws, id),
    queryFn: () => request<ItemDetail>(`/items/${id}`, { workspace: ws }),
    refetchInterval: (q) => (q.state.data?.enrichmentStatus === "running" ? 3_000 : false),
  });

/** Asks the AI to read an item's photos and fill in its empty details. */
export const requestEnrich = (ws: string, itemId: string) =>
  request<{ enrichmentStatus: "running"; jobId: string }>(`/items/${itemId}/enrich`, { method: "POST", workspace: ws });

/** Polls while anything is waiting or working, so posts show up as they land. */
export const useCrosslist = (ws: string, id: string) =>
  useQuery({
    queryKey: keys.crosslist(ws, id),
    queryFn: () => request<CrosslistState>(`/items/${id}/crosslist`, { workspace: ws }),
    refetchInterval: (q) => (q.state.data?.jobs.some((j) => j.status === "queued" || j.status === "running") ? 5_000 : false),
  });

export const useDevices = (ws: string) =>
  useQuery({ queryKey: keys.devices(ws), queryFn: () => request<ExtensionDevice[]>("/extension/devices", { workspace: ws }), refetchInterval: 30_000 });

export const useUnmatchedSales = (ws: string, enabled = true) =>
  useQuery({ queryKey: keys.unmatched(ws), queryFn: () => request<UnmatchedSale[]>("/unmatched-sales", { workspace: ws }), enabled });

/** Polls while a shop reading waits or runs, so its progress shows. */
export const useShops = (ws: string) =>
  useQuery({
    queryKey: keys.shops(ws),
    queryFn: () => request<ShopsState>("/shops", { workspace: ws }),
    refetchInterval: (q) => (q.state.data?.marketplaces.some((m) => m.latest?.status === "requested" || m.latest?.status === "running") ? 5_000 : false),
  });

export const useShopReport = (ws: string) =>
  useQuery({ queryKey: keys.shopReport(ws), queryFn: () => request<ShopReport>("/shops/report", { workspace: ws }) });

export const useSales = (ws: string, shipped: "all" | "pending" | "shipped", enabled = true) =>
  useQuery({ queryKey: [...keys.sales(ws), shipped], queryFn: () => request<SaleRow[]>("/sales", { workspace: ws, query: { shipped } }), enabled });

export const useFinance = (ws: string, from: string, to: string, includePersonal: boolean, enabled = true) =>
  useQuery({
    enabled,
    queryKey: [...keys.finance(ws), from, to, includePersonal],
    queryFn: () => request<FinanceSummary>("/finance/summary", { workspace: ws, query: { from, to, includePersonal } }),
  });

/**
 * A mutation on one item whose response is the item's new detail: the cache
 * is updated in place and the lists that show it are refreshed.
 */
export function useItemMutation<V>(ws: string, id: string, run: (vars: V) => Promise<ItemDetail>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (detail) => {
      qc.setQueryData(keys.item(ws, id), detail);
      void qc.invalidateQueries({ queryKey: keys.items(ws) });
      void qc.invalidateQueries({ queryKey: keys.sales(ws) });
      void qc.invalidateQueries({ queryKey: keys.finance(ws) });
    },
  });
}

async function put(url: string, blob: Blob, contentType: string): Promise<void> {
  const res = await fetch(url, { method: "PUT", body: blob, headers: { "content-type": contentType } });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
}

/** Ticket from the API, both files straight to S3, then confirm. Privacy defaults by slot (a price tag starts private). */
export async function uploadPhoto(ws: string, itemId: string, slot: SlotKey, photo: PreparedPhoto, isPrivate?: boolean): Promise<Photo> {
  const ticket = await request<PhotoUploadTicket>(`/items/${itemId}/photos`, {
    method: "POST",
    workspace: ws,
    body: {
      slot, contentType: photo.contentType, bytes: photo.master.size, thumbBytes: photo.thumb.size,
      width: photo.width, height: photo.height, isPrivate,
    },
  });
  await Promise.all([
    put(ticket.uploadUrl, photo.master, photo.contentType),
    put(ticket.thumbUploadUrl, photo.thumb, "image/webp"),
  ]);
  return request<Photo>(`/items/${itemId}/photos/${ticket.photoId}/complete`, { method: "POST", workspace: ws });
}
